import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/Logo";

// Polls /api/billing/status/:session_id until payment_status === 'paid'.
export function BillingSuccess() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const nav = useNavigate();
  const sessionId = sp.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          setStatus("paid");
          return;
        }
        if (attempts >= 15) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts >= 15) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full border-2 border-border bg-card p-8 text-center space-y-4">
        <div className="flex justify-center">
          {status === "checking" && <Loader2 className="h-10 w-10 animate-spin text-orange-600" />}
          {status === "paid" && <CheckCircle2 className="h-10 w-10 text-emerald-600" />}
          {(status === "timeout" || status === "error") && <XCircle className="h-10 w-10 text-red-600" />}
        </div>
        <LogoMark size={36} className="text-foreground mx-auto" />
        {status === "checking" && (
          <>
            <h1 className="font-heading text-2xl font-black tracking-tight">Wrapping up your subscription…</h1>
            <p className="text-sm text-muted-foreground">Stripe is confirming payment. This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <h1 className="font-heading text-2xl font-black tracking-tight">You're in! 🎉</h1>
            <p className="text-sm text-muted-foreground">
              Your 7-day trial has started. Cancel any time from the billing portal — no charge until day 8.
            </p>
            <Button onClick={() => nav("/dashboard")} data-testid="billing-success-go" className="rounded-none w-full h-11 bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-[0.15em] text-xs font-bold">
              Head to dashboard
            </Button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <h1 className="font-heading text-2xl font-black tracking-tight">Hmm, taking longer than usual</h1>
            <p className="text-sm text-muted-foreground">
              Your card may still succeed — check the Billing page in a minute. If you were charged, the trial will activate automatically.
            </p>
            <Button onClick={() => nav("/billing")} className="rounded-none w-full h-11 border-2 uppercase tracking-[0.15em] text-xs font-bold" variant="outline">
              Back to billing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full border-2 border-border bg-card p-8 text-center space-y-4">
        <XCircle className="h-10 w-10 text-muted-foreground mx-auto" />
        <LogoMark size={36} className="text-foreground mx-auto" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground">No charge was made. Head back to pick a plan whenever you're ready.</p>
        <Button asChild className="rounded-none w-full h-11 bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-[0.15em] text-xs font-bold">
          <Link to="/billing" data-testid="billing-cancel-back">Back to plans</Link>
        </Button>
      </div>
    </div>
  );
}
